import { RootInputNumber, RootTextArea } from "@/components/BaseComponents/Inputs";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTag from "@/components/StatusTag/StatusTag";
import styles from "@/components/UsageForm/usageForm.module.scss";
import { IUsageForm } from "@/pages/products/usage/usageModal";
import { ProductUsageOverview } from "@/pages/products/usage/useUsage";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Col, Form, FormInstance, Popconfirm, Row, Space } from "antd";
import { useWatch } from "antd/es/form/Form";
import React, { useEffect } from "react";
import ActionsPopOverItem from "../ActionsPopOver/ActionsPopOverItem";
import ConsumableModal from "../ConsumableModal/ConsumableModal";
import Container from "../ContentContainer/Container";
import InnerContainer from "../ContentContainer/InnerContainer";
import CrossReference from "../links/CrossReference/CrossReference";
import MoreButton from "../MoreButton/MoreButton";

function UsageForm({
  selectedProducts,
  form,
  onSubmit,
  data,
  isLoading,
  onValuesChange,
  topRight,
  showStatuses,
  disabledText,
}: {
  selectedProducts: ProductUsageOverview[] | number[];
  form: FormInstance<IUsageForm>;
  onSubmit?: () => void;
  data?: IUsageForm;
  isLoading: boolean;
  onValuesChange?: (changedValues: Partial<IUsageForm>) => void;
  topRight?: React.ReactNode;
  showStatuses?: boolean;
  disabledText?: React.ReactNode;
}) {
  const utilitiesUsed = useWatch("utilities_used", form);
  const consumablesUsed = useWatch("consumables_used", form);
  const consumables = useWatch("consumables", form);

  const isSingleProduct = selectedProducts.length === 1;

  useEffect(() => {
    if (data && isSingleProduct) {
      form.setFieldsValue({
        ...data,
      });
    } else {
      form.setFieldsValue({ utilities_used: null, consumables_used: null });
      form.resetFields();
    }
  }, [data, selectedProducts]);

  const onUtilitiesUsedChange = (value: boolean) => {
    const newValue = value === utilitiesUsed ? null : value;

    if (!newValue) {
      onValuesChange?.({
        utilities_used: newValue,
        electricity_amount: null,
        electricity_notes: null,
      });
      form.setFieldsValue({
        utilities_used: newValue,
        electricity_amount: null,
        electricity_notes: null,
      });
    } else {
      onValuesChange?.({
        utilities_used: newValue,
      });
      form.setFieldsValue({ utilities_used: newValue });
    }
  };

  const onConsumablesUsedChange = (value: boolean) => {
    const newValue = value === consumablesUsed ? null : value;

    onValuesChange?.({ consumables_used: newValue, consumables: [] });
    form.setFieldsValue({ consumables_used: newValue, consumables: [] });
  };

  const utilitiesStatus = !showStatuses ? (
    <></>
  ) : isLoading ? (
    <Skeleton style={{ width: "90px", height: "24px" }} />
  ) : (utilitiesUsed === true && form.getFieldValue("electricity_amount")) ||
    utilitiesUsed === false ? (
    <StatusTag
      status="complete"
      showIcon={false}
    />
  ) : (
    <StatusTag
      status="incomplete"
      showIcon={false}
    />
  );

  const consumablesStatus = !showStatuses ? (
    <></>
  ) : isLoading ? (
    <Skeleton style={{ width: "90px", height: "24px" }} />
  ) : (consumablesUsed === true && !!consumables?.length) || consumablesUsed === false ? (
    <StatusTag
      status="complete"
      showIcon={false}
    />
  ) : (
    <StatusTag
      status="incomplete"
      showIcon={false}
    />
  );

  const UsageSelectionButtonGroup = ({
    used,
    onChange,
    yesConfirmText,
    noConfirmText,
  }: {
    used: boolean | null;
    onChange: (value: boolean) => void;
    yesConfirmText?: string;
    noConfirmText?: string;
  }) => {
    const description = <p>This action will remove any data already provided.</p>;
    return (
      <>
        {isLoading && <Skeleton style={{ width: 68, height: 23 }} />}
        {!isLoading && (
          <Space.Compact>
            {!!topRight && used === true ? (
              <Popconfirm
                title={yesConfirmText}
                onConfirm={() => onChange(true)}
                description={description}
                icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
                okText={"Yes, reset"}
              >
                <Button
                  size={"small"}
                  className={disabledText && styles.disabledPrimaryButton}
                  type={"primary"}
                  disabled={!!disabledText}
                >
                  Yes
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size={"small"}
                className={disabledText && used === true ? styles.disabledPrimaryButton : ""}
                type={used === true ? "primary" : "default"}
                onClick={() => onChange(true)}
                disabled={!!disabledText}
              >
                Yes
              </Button>
            )}
            {!!topRight && used === true ? (
              <Popconfirm
                title={noConfirmText}
                onConfirm={() => onChange(false)}
                description={description}
                icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
                okText={"Yes, turn off"}
              >
                <Button
                  size={"small"}
                  type={"default"}
                  disabled={!!disabledText}
                >
                  No
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size={"small"}
                className={disabledText && used === false ? styles.disabledPrimaryButton : ""}
                type={used === false ? "primary" : "default"}
                onClick={() => onChange(false)}
                disabled={!!disabledText}
              >
                No
              </Button>
            )}
          </Space.Compact>
        )}
      </>
    );
  };

  return (
    <Form
      form={form}
      layout={"vertical"}
      onFinish={onSubmit}
      initialValues={{ consumables: [] }}
      onValuesChange={(_, allValues) => onValuesChange?.(allValues)}
    >
      <div>
        {/*HEADER*/}
        <Container
          padding={false}
          noRoundedTop
        >
          <InnerContainer
            title="Usage"
            right={topRight}
          >
            <Row gutter={[10, 20]}>
              <Col span={24}>
                <span>
                  Specify your product's total electricity or consumables being used over it's
                  lifespan.
                </span>
              </Col>
              {selectedProducts.length > 1 && (
                <Col span={24}>
                  <Space className={styles.infoMessage}>
                    <InfoCircleOutlined />
                    <span>
                      With this bulk action all existing usage data for the selected products will
                      be overwritten
                    </span>
                  </Space>
                </Col>
              )}
            </Row>

            {/*LIFESPAN*/}
            <Row
              className={`${styles.section} ${styles.lifespanSection}`}
              gutter={[10, 10]}
            >
              <Col
                span={24}
                className={styles.heading}
              >
                Lifespan
              </Col>
              <Col span={24}>
                <Row gutter={[16, 10]}>
                  <Col flex={"none"}>
                    <Form.Item
                      name={"lifespan"}
                      label={
                        <span>
                          Lifespan <span className={styles.optional}>(optional)</span>
                        </span>
                      }
                      style={{ marginBottom: 0 }}
                    >
                      <RootInputNumber
                        suffix={"months"}
                        style={{ width: "260px" }}
                        showSkeleton={isLoading}
                        disabled={!!disabledText}
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex={"auto"}>
                    <Form.Item
                      name={"lifespan_notes"}
                      label={
                        <span>
                          Notes and documentation{" "}
                          <span className={styles.optional}>(optional)</span>
                        </span>
                      }
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          max: 1000,
                          message: "notes must be less than 1000 characters.",
                        },
                      ]}
                    >
                      <RootTextArea
                        autoSize
                        style={{ maxWidth: "900px", minWidth: "300px" }}
                        showSkeleton={isLoading}
                        disabled={!!disabledText}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
            {/*UTILITIES*/}
            <Row
              className={`${styles.section} ${styles.utilitiesSection}`}
              gutter={[10, 10]}
            >
              <Col span={24}>
                <Row>
                  <Col span={24}>
                    <Row align={"middle"}>
                      <Col flex={"auto"}>
                        <Space>
                          <span className={styles.heading}>Electricity used</span>
                          <Form.Item
                            name={"utilities_used"}
                            style={{ marginBottom: 0 }}
                          >
                            <UsageSelectionButtonGroup
                              used={utilitiesUsed}
                              onChange={onUtilitiesUsedChange}
                              yesConfirmText={"Are you sure you want to reset electricity used?"}
                              noConfirmText={"Are you sure you want to turn off electricity used?"}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                      <Col flex={"none"}>{utilitiesStatus}</Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <span className={styles.description}>
                      Calculate the average amount of electricity used over the total product
                      lifespan.
                    </span>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                {utilitiesUsed === true && (
                  <Row gutter={[16, 10]}>
                    <Col flex={"none"}>
                      <Form.Item
                        name={"electricity_amount"}
                        label={
                          <span>
                            Total consumed over lifespan{" "}
                            <span className={styles.optional}>(optional)</span>
                          </span>
                        }
                        style={{ marginBottom: 0 }}
                      >
                        <RootInputNumber
                          suffix={"kWh"}
                          style={{ width: "260px" }}
                          disabled={!!disabledText}
                          controls={false}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex={"auto"}>
                      <Form.Item
                        name={"electricity_notes"}
                        label={
                          <span>
                            Notes and documentation{" "}
                            <span className={styles.optional}>(optional)</span>
                          </span>
                        }
                        style={{ marginBottom: 0 }}
                        rules={[
                          {
                            max: 1000,
                            message: "notes must be less than 1000 characters.",
                          },
                        ]}
                      >
                        <RootTextArea
                          style={{ maxWidth: "900px", minWidth: "300px" }}
                          disabled={!!disabledText}
                          autoSize
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>

            {/*CONSUMABLES*/}
            <Row
              className={`${styles.section} ${styles.consumablesSection}`}
              gutter={[10, 10]}
            >
              <Col span={24}>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col flex={"auto"}>
                        <Space>
                          <span className={styles.heading}>Consumables used</span>
                          <Form.Item
                            name={"consumables_used"}
                            style={{ marginBottom: 0 }}
                          >
                            <UsageSelectionButtonGroup
                              used={consumablesUsed}
                              onChange={onConsumablesUsedChange}
                              yesConfirmText={"Are you sure you want to reset consumables used?"}
                              noConfirmText={"Are you sure you want to turn off consumables used?"}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                      <Col flex={"none"}>{consumablesStatus}</Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row
                      wrap={false}
                      align={"top"}
                      gutter={[16, 10]}
                    >
                      <Col
                        flex={"auto"}
                        style={{ minHeight: "24px" }}
                      >
                        <span className={styles.description}>
                          Calculate the average amount of consumables used over the product lifespan
                          and specify this with an impact reference and end of life impact.
                        </span>
                      </Col>
                      <Col flex={"none"}>
                        {consumablesUsed === true && <div style={{ marginTop: 20 }}></div>}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                {consumablesUsed === true && (
                  <Row gutter={[5, 5]}>
                    <Col
                      span={24}
                      className={styles.table}
                    >
                      <Form.List name="consumables">
                        {(fields, { add, remove }) => (
                          <>
                            <div className={[styles.row, styles.header].join(" ")}>
                              <div>Consumable</div>
                              <div>Amount</div>
                              <div>Unit</div>
                              <div>Notes</div>
                              <div className={styles.right}>
                                <ConsumableModal
                                  disabled={!!disabledText}
                                  onChange={(consumable) => add(consumable)}
                                  totalConsumabedLabel="Total consumed in product lifetime"
                                  resetOnClose
                                >
                                  <Button
                                    disabled={!!disabledText}
                                    type="primary"
                                    size="small"
                                  >
                                    Add
                                  </Button>
                                </ConsumableModal>
                              </div>
                            </div>
                            {fields.map((field, index) => {
                              return (
                                <div
                                  className={styles.row}
                                  key={field.key}
                                >
                                  <div>
                                    <CrossReference
                                      to={
                                        "/consumables/" +
                                        form.getFieldValue(["consumables", field.name, "id"])
                                      }
                                    >
                                      {form.getFieldValue(["consumables", field.name, "name"])}
                                    </CrossReference>
                                  </div>
                                  <div>
                                    {form.getFieldValue(["consumables", field.name, "amount"])}
                                  </div>
                                  <div>
                                    {form.getFieldValue(["consumables", field.name, "unit"])}
                                  </div>
                                  <div>
                                    {form.getFieldValue(["consumables", field.name, "notes"])}
                                  </div>
                                  <div className={styles.right}>
                                    {!disabledText && (
                                      <MoreButton
                                        buttonProps={{ type: "text" }}
                                        actions={
                                          <>
                                            <Form.Item
                                              {...field}
                                              noStyle
                                            >
                                              <ConsumableModal
                                                onChange={(value) =>
                                                  form.setFieldValue(
                                                    ["consumables", field.name],
                                                    value
                                                  )
                                                }
                                                value={form.getFieldValue([
                                                  "consumables",
                                                  field.name,
                                                ])}
                                                totalConsumabedLabel="Total consumed in product lifetime"
                                                headerText="Edit consumable"
                                                okText="Save"
                                              >
                                                <ActionsPopOverItem icon={<EditOutlined />}>
                                                  Edit
                                                </ActionsPopOverItem>
                                              </ConsumableModal>
                                            </Form.Item>
                                            <ActionsPopOverItem
                                              onClick={() => remove(index)}
                                              icon={<DeleteOutlined />}
                                              danger
                                            >
                                              Delete
                                            </ActionsPopOverItem>
                                          </>
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </InnerContainer>
        </Container>
      </div>
    </Form>
  );
}

export default UsageForm;
